import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  VisualHistoryQuery,
  VisualHistoryQueryVariables,
} from 'molbio/visual-history/__generated/VisualHistoryQuery';
import VisualHistoryEventList from 'molbio/visual-history/VisualHistoryEventList';
import {VISUAL_HISTORY_EVENT_LIST_ITEM_FRAGMENT} from 'molbio/visual-history/VisualHistoryEventListItem';
import {VISUAL_HISTORY_EVENT_METADATA_FIELD_FRAGMENT} from 'molbio/visual-history/VisualHistoryEventMetadataField';
import {
  VISUAL_HISTORY_EVENT_SOURCE_INFO_FRAGMENT,
  VISUAL_HISTORY_POLYMER_MAP_FRAGMENT,
} from 'molbio/visual-history/VisualHistoryEventSource';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

type VisualHistoryGQL = ExtractByTypename<VisualHistoryQuery['visualHistory'], 'VisualHistoryNode'>;

export interface VisualHistoryTabContentsProps {
  entityId: string;
  entityType: string;
  entityVersionId: number;
  onClickVersion: (entityVersionId: number) => void;
}

export const VISUAL_HISTORY_TAB_CONTENTS_QUERY = gql`
  query VisualHistoryQuery($entityVersionId: EntityVersionIdInputNode!)
  @engComponent(name: MOLBIO)
  @skipAudit {
    visualHistory(entityVersionId: $entityVersionId) {
      ... on VisualHistoryNode {
        events {
          ...VisualHistoryEventListItemFragment
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${VISUAL_HISTORY_EVENT_LIST_ITEM_FRAGMENT}
  ${VISUAL_HISTORY_EVENT_METADATA_FIELD_FRAGMENT}
  ${VISUAL_HISTORY_EVENT_SOURCE_INFO_FRAGMENT}
  ${VISUAL_HISTORY_POLYMER_MAP_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Render the contents of the visual history tab for the given entity.
 * @param entityId Id of the entity.
 * @param entityType Type of the entity.
 * @param entityVersionId Id of the specific version of the entity to render history for.
 * @param onClickVersion Callback invoked when a user clicks on a specific version.
 * @constructor
 */
export default function VisualHistoryTabContents({
  entityId,
  entityType,
  entityVersionId,
  onClickVersion,
}: VisualHistoryTabContentsProps): JSX.Element {
  const visualHistoryResult = useBenchlingQuery<VisualHistoryQuery, VisualHistoryQueryVariables>(
    VISUAL_HISTORY_TAB_CONTENTS_QUERY,
    {
      variables: {
        entityVersionId: {
          entityId,
          entityType,
          // For unclear reasons, visual history data is queried with a string-ified version ID.
          entityVersionId: entityVersionId.toString(),
        },
      },
    }
  );
  let contents;
  if (visualHistoryResult.type === QueryResultType.PENDING) {
    contents = <HorizontalThrobber />;
  } else if (visualHistoryResult.type === QueryResultType.SUCCESS) {
    const visualHistory: VisualHistoryGQL | null = !isInaccessible(visualHistoryResult.data.visualHistory)
      ? visualHistoryResult.data.visualHistory
      : null;
    if (visualHistory != null) {
      contents = (
        <VisualHistoryEventList
          events={visualHistory.events}
          onClickVersion={(versionId: string) => {
            // For unclear reasons, visual history events store version IDs as a string-ified number
            onClickVersion(Number.parseInt(versionId, 10));
          }}
        />
      );
    } else {
      contents = (
        <InlineBanner
          text={"You don't have permissions to view visual history for this item."}
          bannerType={InlineBannerType.WARNING}
        />
      );
    }
  } else if (visualHistoryResult.type === QueryResultType.ERROR) {
    contents = (
      <InlineBanner
        text="There was an error loading visual history for this item."
        bannerType={InlineBannerType.ERROR}
      />
    );
  }
  return <div className={css(styles.contentsContainer)}>{contents}</div>;
}

const styles = StyleSheet.create({
  contentsContainer: {
    padding: '8px 8px 8px 24px',
  },
});
